/** GA OpenAI Realtime API helpers (WebRTC + client events). */

export const REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';
export const DEFAULT_REALTIME_MODEL = 'gpt-realtime-mini';

export type GaRealtimeTool = {
  type: 'function';
  name: string;
  description: string;
  parameters: Record<string, unknown>;
};

/** Task 2 examiner tools — unchanged semantics. */
export function buildTask2RealtimeTools(): GaRealtimeTool[] {
  return [
    {
      type: 'function',
      name: 'prompt_continue',
      description: 'After AI reply, if user is silent for 10s then use the tool',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      type: 'function',
      name: 'check_user_silence',
      description: 'Observe user silence for duration ms; may prompt and schedule end',
      parameters: {
        type: 'object',
        properties: {
          duration: { type: 'integer', description: 'Silence duration in ms' },
        },
        required: ['duration'],
      },
    },
    {
      type: 'function',
      name: 'end_task',
      description: 'End the task cleanly; UI will play static ending audio',
      parameters: {
        type: 'object',
        properties: {
          delay: { type: 'integer', description: 'Delay in ms before ending' },
        },
        required: ['delay'],
      },
    },
    {
      type: 'function',
      name: 'thank_you_flow',
      description:
        'If the user expresses thanks/closure (e.g., "merci", "merci beaucoup", "je n\'ai plus de questions"), then run this. If remaining_seconds > 45: wait ~2s, play a brief "still time left" prompt; if ~4s more silence, end. If remaining_seconds ≤ 45: end after a short pause (~2s) to allow hesitant speakers.',
      parameters: {
        type: 'object',
        properties: { remaining_seconds: { type: 'integer' } },
        required: [],
      },
    },
  ];
}

export function buildTask2TopicExtra(topic: string): string {
  return `You are playing the role implied by this scenario: "${topic}".

              ALWAYS RESPOND IN FRENCH ONLY.
              Stay strictly within the facts and context provided by this scenario and the base instructions.
              Answer the learner's latest clear question directly.

              FOR EVERY ANSWER:
              - Exactly ONE sentence.
              - Aim for 7 to 12 words.
              - Never add a second sentence, even if the user question is long.
              - Keep the tone warm, natural, and spoken, not academic.
              
              If the user has not yet asked a clear question (they only say they are interested or want to know more), reply with a very short opener inviting a question. For example:
              - "Bien sûr, que voudriez-vous savoir ?"
              - "Oui, bien sûr, qu'aimeriez-vous savoir ?"
              - "D'accord, qu'aimeriez-vous savoir exactement ?"`;
}

export function buildGaSessionUpdatePayload(options: {
  model: string;
  instructions?: string;
  voice?: string;
  tools?: GaRealtimeTool[];
  includeTools?: boolean;
}): Record<string, unknown> {
  const { model, instructions, voice, tools, includeTools = true } = options;
  const session: Record<string, unknown> = {
    type: 'realtime',
    model,
    output_modalities: ['audio'],
    audio: {
      input: {
        turn_detection: {
          type: 'server_vad',
          threshold: 0.8,
          prefix_padding_ms: 200,
          silence_duration_ms: 1400,
          create_response: true,
          interrupt_response: false,
        },
      },
      output: voice ? { voice } : {},
    },
  };

  if (instructions) session.instructions = instructions;
  if (includeTools && tools?.length) {
    session.tools = tools;
    session.tool_choice = 'auto';
  }

  return { type: 'session.update', session };
}

/** Ephemeral key from edge (GA client_secrets) or legacy beta field. */
export function extractRealtimeClientSecret(initJson: Record<string, unknown>): string | null {
  if (typeof initJson.token === 'string' && initJson.token) return initJson.token;
  if (typeof initJson.value === 'string' && initJson.value) return initJson.value;
  const cs = initJson.client_secret as Record<string, unknown> | undefined;
  if (typeof cs?.value === 'string' && cs.value) return cs.value;
  return null;
}

export function extractRealtimeModel(
  initJson: Record<string, unknown>,
  fallback?: string
): string {
  const session = initJson.session as Record<string, unknown> | undefined;
  if (typeof session?.model === 'string' && session.model) return session.model;
  if (typeof initJson.model === 'string' && initJson.model) return initJson.model;
  return fallback || DEFAULT_REALTIME_MODEL;
}

export function maskSecret(value: string): string {
  if (!value || value.length < 12) return '(empty or short)';
  return `${value.slice(0, 8)}…${value.slice(-4)} (len=${value.length})`;
}
